import {
  type SanityCombinedListingConfiguration,
  type SanityCombinedListingSettings,
} from '@data/sanity/queries/types/product'
import {
  getPassepartoutHoleSizeFromArtworkSize,
  getSizeDimensionsFromString,
  getSizeString,
} from '@lib/size'
import {
  findCombinedListingProducts,
  getCombinedListingVariants,
} from './product'

/**
 * Gets combined listing product variant SKU.
 */
export const getCombinedListingProductVariantSku = (
  combinedListingSettings: SanityCombinedListingSettings,
  combinedListingConfiguration: SanityCombinedListingConfiguration,
  productIds: number[],
  frameSize: string,
  artworkSize?: string,
) => {
  // Find products
  const products = findCombinedListingProducts(
    combinedListingConfiguration,
    productIds,
  )
  const frameProduct = products.find(
    (product) => product.combinedListingProductType === 'frame',
  )
  const glassProduct = products.find(
    (product) => product.combinedListingProductType === 'glass',
  )
  const backboardProduct = products.find(
    (product) => product.combinedListingProductType === 'backboard',
  )
  const passepartoutProduct = products.find(
    (product) => product.combinedListingProductType === 'passepartout',
  )
  const spacerProduct = products.find(
    (product) => product.combinedListingProductType === 'spacer',
  )

  // Find variants
  const variants = getCombinedListingVariants(products, frameSize)
  const frameVariant = variants.find((variant) => {
    return frameProduct?.variants?.some(
      (frameVariant) => frameVariant.variantID === variant.variantID,
    )
  })
  const glassVariant = variants.find((variant) => {
    return glassProduct?.variants?.some(
      (glassVariant) => glassVariant.variantID === variant.variantID,
    )
  })
  const backboardVariant = variants.find((variant) => {
    return backboardProduct?.variants?.some(
      (backboardVariant) => backboardVariant.variantID === variant.variantID,
    )
  })
  const passepartoutVariant = variants.find((variant) => {
    return passepartoutProduct?.variants?.some(
      (passepartoutVariant) =>
        passepartoutVariant.variantID === variant.variantID,
    )
  })
  const spacerVariant = variants.find((variant) => {
    return spacerProduct?.variants?.some(
      (spacerVariant) => spacerVariant.variantID === variant.variantID,
    )
  })

  // Calculate passepartout cut-out size
  let passepartoutHoleSize: string | undefined

  if (passepartoutProduct && passepartoutVariant && artworkSize) {
    const [artworkWidth, artworkHeight] =
      getSizeDimensionsFromString(artworkSize)
    const [passepartoutHoleWidth, passepartoutHoleHeight] =
      getPassepartoutHoleSizeFromArtworkSize(artworkWidth, artworkHeight)
    passepartoutHoleSize = getSizeString(
      passepartoutHoleWidth,
      passepartoutHoleHeight,
      {
        valueSeparator: 'X',
        decimalSeparator: '.',
      },
    )
  }

  // Check if there is no frame SKU
  if (!frameVariant?.sku) {
    return
  }

  const skus = [
    // 1. Profile SKU
    // 2. Frame size
    frameVariant?.sku,

    // 3. Glass SKU
    glassProduct?.combinedListingSku ??
      glassVariant?.sku ??
      combinedListingSettings.noGlassSku,

    // 4. Backboard SKU
    backboardProduct?.combinedListingSku ??
      backboardVariant?.sku ??
      combinedListingSettings.noBackboardSku,

    // 5. Passepartout SKU
    passepartoutProduct?.combinedListingSku ??
      passepartoutVariant?.sku ??
      combinedListingSettings.noPassepartoutSku,

    // 6. Spacer SKU
    spacerProduct?.combinedListingSku ??
      spacerVariant?.sku ??
      combinedListingSettings.noSpacerSku,

    // 7. Passepartout cut-out size
    passepartoutHoleSize ? `[${passepartoutHoleSize}]` : '',
  ]

  return skus.filter(Boolean).join('')
}
